/**
 * Хуки данных шахматки номеров (manager v3 §4.1). Server-state только через
 * React Query (патент проекта) поверх готового бэка:
 *
 *   GET /v1/rooms/calendar?from=&to=  → per-room occupancies (bulk)
 *   GET /v1/public/{slug}/room-types  → имена типов (type_id → name)
 *
 * Окно [from, to) — половинно-открытое, как и на бэке (stay.check_in < to
 * AND stay.check_out > from). Список дней строится включительно [from, to-1].
 */
import { useQuery } from '@tanstack/react-query';
import { getPublicRoomTypes, getRoomsCalendar } from '@hsc/api-client';
import type { CalendarResponse, PublicRoomTypesResponse } from '@hsc/types';
import { isoDate } from '../../lib/fleet-format.js';

export function useChessboardCalendar(from: string, to: string) {
  return useQuery<CalendarResponse>({
    queryKey: ['rooms', 'chessboard', from, to],
    queryFn: () => getRoomsCalendar(from, to),
  });
}

export function useRoomTypeNames(slug: string | null) {
  return useQuery<PublicRoomTypesResponse>({
    queryKey: ['room-types', slug],
    queryFn: () => getPublicRoomTypes(slug as string),
    enabled: Boolean(slug),
    staleTime: 5 * 60_000,
  });
}

/** Один день колонки шахматки. */
export interface DayColumn {
  /** YYYY-MM-DD (локальная дата начала дня). */
  iso: string;
  /** День месяца (1–31). */
  day: number;
  /** Сокращение дня недели: Пн…Вс. */
  weekday: string;
  /** ДД.ММ для подписи. */
  short: string;
  isWeekend: boolean;
  isToday: boolean;
}

const WEEKDAY_SHORT = ['Вс', 'Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб'];

/** Строит список дней [from, to) включительно по дням (to — эксклюзивно). */
export function buildDays(fromIso: string, toIso: string): DayColumn[] {
  const out: DayColumn[] = [];
  const todayIso = isoDate(new Date());
  const start = new Date(`${fromIso}T00:00:00`);
  const end = new Date(`${toIso}T00:00:00`);
  const cur = new Date(start);
  // Защита от бесконечного цикла при некорректном диапазоне.
  let guard = 0;
  while (cur < end && guard < 400) {
    const iso = isoDate(cur);
    const dow = cur.getDay();
    out.push({
      iso,
      day: cur.getDate(),
      weekday: WEEKDAY_SHORT[dow] ?? '',
      short: cur.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' }),
      isWeekend: dow === 0 || dow === 6,
      isToday: iso === todayIso,
    });
    cur.setDate(cur.getDate() + 1);
    guard += 1;
  }
  return out;
}
